import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ILogisticServiceEditionForm } from '../../LogisticServiceEdition.types';
import { useEffect } from 'react';
import BordTooltip from 'components/BordTooltip/BordTooltip';
import { BordOneToneIcon, NudosMaskedNumberInput } from 'components/NudosComponents';
import { nudosTextcolor } from 'styles/styles.constants';
import { CURRENCIES } from 'types/bussines';
import { formatCost } from 'utils/formatNumbers';
import { VAT_VALUE_BY_COUNTRY } from '../../../../../utils/productDefinitions';
import { ILogisticServiceWithProductPrices } from '../../../../../types/services';
import './LSEditionTotals.scss';

interface ILSEditionTotalsProps {
  logisticServiceData?: ILogisticServiceWithProductPrices;
  currency: string;
  hasTaxes: boolean;
  hasCommission: boolean;
}

const LSEditionTotals = ({ logisticServiceData, currency, hasTaxes, hasCommission }: ILSEditionTotalsProps) => {
  const { setValue, control } = useFormContext<ILogisticServiceEditionForm>();
  const products = useWatch({ control, name: 'products' }) || [];
  const iva = useWatch({ control, name: 'iva' }) || 0;
  const subtotal = useWatch({ control, name: 'subtotal' }) || 0;
  const total = useWatch({ control, name: 'totalUsd' }) || 0;
  const totalLocal = useWatch({ control, name: 'totalLocal' }) || 0;
  const commissionUsd = useWatch({ control, name: 'commissionUsd' }) || 0;
  const exchangeRate = useWatch({ control, name: 'exchangeRate' }) || 0;

  const billingCountry = logisticServiceData?.billingData;
  const countryTax = VAT_VALUE_BY_COUNTRY[billingCountry?.countryCode || 'uy'];
  const taxRate = countryTax ? countryTax / 100 : 0;

  const updateTotal = () => {
    const calcTotal = products.reduce((acc, product) => {
      if (!product.logisticServiceProductPrices) return acc;
      return (
        acc +
        product.logisticServiceProductPrices.reduce((acc, service) => acc + (Number(service?.totalValueUsd) || 0), 0)
      );
    }, 0);
    const totalWithPaymentCommission = calcTotal + commissionUsd;
    setValue('totalUsd', totalWithPaymentCommission);
    return calcTotal;
  };

  const updateLocalTotal = () => {
    if (currency === CURRENCIES.USD) return;

    const calcTotalLocal = products.reduce((acc, product) => {
      if (!product.logisticServiceProductPrices) return acc;
      return (
        acc +
        product.logisticServiceProductPrices.reduce((acc, service) => acc + (Number(service?.totalValueLocal) || 0), 0)
      );
    }, 0);
    const totalWithPaymentCommissionLocal = calcTotalLocal + commissionUsd * exchangeRate;
    setValue('totalLocal', totalWithPaymentCommissionLocal);
  };

  const updateTaxes = (total: number) => {
    if (!hasTaxes) {
      setValue('iva', 0);
      return 0;
    }
    const taxes = total * taxRate;
    setValue('iva', -taxes);
    return taxes;
  };

  const handleChangeOfServices = () => {
    const total = updateTotal();
    const taxes = updateTaxes(total);
    updateLocalTotal();
    setValue('subtotal', total + taxes);
  };

  useEffect(() => {
    handleChangeOfServices();
  }, [products, commissionUsd]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="editionTotals">
      <p className="subtotal">
        <span>Subtotal</span>
        <span>${formatCost(Number(subtotal))} USD</span>
      </p>
      {hasTaxes && (
        <div className="iva">
          <div className="ivaLabel">
            IVA (-{countryTax}%) &nbsp;
            <BordTooltip
              message={
                <>
                  <br />
                  Para las razones sociales en Zona Franca <br /> no cobramos el impuesto de {countryTax}%
                </>
              }
            >
              <BordOneToneIcon variant="info" customWidth="12px" stroke={nudosTextcolor} standardSize={52} />
            </BordTooltip>
          </div>
          <span>${iva.toFixed(2)} USD</span>
        </div>
      )}
      {hasCommission && (
        <div className="paymentCommission">
          <span>Comisión de pago</span>
          <Controller
            name="commissionUsd"
            control={control}
            render={({ field }) => (
              <NudosMaskedNumberInput
                isFilled={true}
                placeholder={`${field.value ? field.value : '0.00'} USD`}
                handleChange={text => field.onChange(Number(text || '0'))}
                currentValue={String(field.value)}
                maskPrefix="$"
                maskSufix=" USD"
                numberOfDecimals={2}
                customClassName="paymentCommissionInput"
              />
            )}
          />
        </div>
      )}
      <hr />
      <p className="totalUsd">
        <span>Total a pagar en USD</span>
        <span>${formatCost(Number(total))} USD</span>
      </p>
      {currency !== CURRENCIES.USD && (
        <p className="totalLocal">
          <span>Total a pagar en {currency}</span>
          <span>
            ${formatCost(Number(totalLocal))} {currency}
          </span>
        </p>
      )}
    </section>
  );
};

export default LSEditionTotals;
